/** @format */

import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { canRollBackImportJob, type SearchParams } from "@question-bank/shared";
import { Locale } from "../../features/i18n/init";
import { useBrand } from "../../hooks/useBrand";
import { useImportJob } from "../../hooks/useImportJob";
import { rollbackImport } from "../../api/imports";
import { getAssetUrl } from "../../api/questions";
import { notify } from "../../components/Toast/toast";
import { importJobStatusLabels } from "../../utils/importJobs";

/** Page size for the filtered Questions list. Matches `DEFAULT_LIMIT` in AdminQuestionsPage. */
const QUESTIONS_PAGE_LIMIT = 20;

interface RollbackConfirmDialogProps {
	isRollingBack: boolean;
	onConfirm: () => void;
	onCancel: () => void;
}

/**
 * Confirmation for a rollback, modelled on DeleteConfirmModal: focus lands on
 * Cancel and Tab is trapped inside the dialog, Escape cancels unless a rollback
 * is in flight, and the page returns focus to the trigger when the dialog closes.
 */
const RollbackConfirmDialog = ({ isRollingBack, onConfirm, onCancel }: RollbackConfirmDialogProps) => {
	const cancelRef = useRef<HTMLButtonElement>(null);
	const dialogRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		cancelRef.current?.focus();
	}, []);

	useEffect(() => {
		const onKey = (event: KeyboardEvent) => {
			if (event.key === "Escape" && !isRollingBack) {
				onCancel();
			}
		};
		document.addEventListener("keydown", onKey);
		return () => document.removeEventListener("keydown", onKey);
	}, [onCancel, isRollingBack]);

	// Focus trap
	useEffect(() => {
		const dialog = dialogRef.current;
		if (!dialog) {
			return;
		}
		const focusable = dialog.querySelectorAll<HTMLElement>(
			'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
		);
		const first = focusable[0];
		const last = focusable[focusable.length - 1];
		const trap = (event: KeyboardEvent) => {
			if (event.key !== "Tab") {
				return;
			}
			if (event.shiftKey ? document.activeElement === first : document.activeElement === last) {
				event.preventDefault();
				(event.shiftKey ? last : first).focus();
			}
		};
		dialog.addEventListener("keydown", trap);
		return () => dialog.removeEventListener("keydown", trap);
	}, [isRollingBack]);

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
			<div
				ref={dialogRef}
				role="alertdialog"
				aria-modal="true"
				aria-labelledby="rollback-dialog-title"
				aria-describedby="rollback-dialog-desc"
				className="w-full max-w-md rounded-lg border border-slate-200 bg-white p-6 shadow-lg"
			>
				<h2 id="rollback-dialog-title" className="text-base font-semibold text-slate-800">
					Roll back this import?
				</h2>
				<p id="rollback-dialog-desc" className="mt-2 text-slate-600">
					Every question this import created will be deleted. Questions edited or published since the import are kept.
					This cannot be undone.
				</p>
				<div className="mt-5 flex justify-end gap-3">
					<button
						ref={cancelRef}
						type="button"
						onClick={onCancel}
						disabled={isRollingBack}
						className="rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
					>
						Cancel
					</button>
					<button
						type="button"
						onClick={onConfirm}
						disabled={isRollingBack}
						className="rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-40"
					>
						{isRollingBack ? "Rolling back…" : "Confirm roll back"}
					</button>
				</div>
			</div>
		</div>
	);
};

export const AdminImportJobDetailsPage = () => {
	const { brand } = useBrand();
	const { locale, id = "" } = useParams<{ locale: Locale; id: string }>();
	const { job, isLoading, error, reload } = useImportJob(brand, id);
	const [isConfirmOpen, setIsConfirmOpen] = useState(false);
	const [isRollingBack, setIsRollingBack] = useState(false);
	const [shouldFocusHeading, setShouldFocusHeading] = useState(false);
	const rollbackButtonRef = useRef<HTMLButtonElement>(null);
	const headingRef = useRef<HTMLHeadingElement>(null);

	// After a rollback trigger button is gone, so focus moves to the heading
	// once the refreshed (rolled-back) job has rendered.

	useEffect(() => {
		if (shouldFocusHeading && job?.status === "rolled_back") {
			headingRef.current?.focus();
			setShouldFocusHeading(false);
		}
	}, [shouldFocusHeading, job]);

	const backLink = `/${locale}/admin/import-jobs`;

	const closeConfirm = () => {
		setIsConfirmOpen(false);
		rollbackButtonRef.current?.focus();
	};

	const handleRollBack = async () => {
		if (!job) {
			return;
		}
		setIsRollingBack(true);
		try {
			await rollbackImport(brand, job.id);
			setIsConfirmOpen(false);
			setShouldFocusHeading(true);
			notify("success", "Import rolled back");
			reload();
		} catch (err) {
			notify("error", err instanceof Error ? err.message : "Roll back failed. Please try again");
		} finally {
			setIsRollingBack(false);
		}
	};

	if (isLoading) {
		return (
			<section className="space-y-6">
				<p role="status" className="text-sm text-slate-600">
					Loading import job…
				</p>
			</section>
		);
	}

	if (error || !job) {
		return (
			<section className="space-y-6">
				<div role="alert" className="rounded-lg border border-red-500 bg-red-100 p-6 shadow-(--admin-shadow-raised)">
					<p className="font-semibold text-red-700"> Failed to load import job</p>
					<p className="mt-1 text-sm text-red-700">{error ?? "Import job not found"}</p>
					<Link
						to={backLink}
						className="mt-4 inline-block text-sm font-semibold text-(--admin-color-primary-strong) hover:underline"
					>
						← Back to import jobs
					</Link>
				</div>
			</section>
		);
	}

	const canRollBack = canRollBackImportJob(job);
	const isStalled = (job.status === "pending" || job.status === "rolling_back") && canRollBack;
	const skippedIds = new Set(job.rollbackSkippedIds ?? []);
	const collisions = job.collisions ?? [];

	const isPdf = job.source === "pdf";
	// A PDF job has no file name and no rows. It shows the keys of its three PDFs instead.
	const pdfLink = (key: string): ReactNode => {
		const url = getAssetUrl(brand, key);
		return (
			<a
				href={url}
				target="_blank"
				rel="noreferrer"
				className="break-all text-(--admin-color-primary-strong) hover:underline"
			>
				{url}
			</a>
		);
	};
	const sourceRows: [string, ReactNode][] =
		isPdf && job.sourceKeys
			? [
					["Paper PDF", pdfLink(job.sourceKeys.paper)],
					["Mark scheme PDF", pdfLink(job.sourceKeys.markScheme)],
					["Specification PDF", pdfLink(job.sourceKeys.specification)],
				]
			: [["File", job.fileName ?? "-"]];
	const detailRows: [string, ReactNode][] = [
		["Status", importJobStatusLabels[job.status]],
		...sourceRows,
		["Subject", job.subject ?? "-"],
		["Paper code", job.paperCode ?? "-"],
		["Imported by", job.createdByEmail ?? job.createdBy],
		["Imported at", new Date(job.createdAt).toLocaleString("en-GB")],
		...(isPdf ? [] : ([["Rows in file", String(job.rowCount)]] as [string, ReactNode][])),
		["Questions created", String(job.createdQuestionIds.length)],
	];
	if (job.failedReason) {
		detailRows.push(["Failure reason", job.failedReason]);
	} else if (isStalled) {
		detailRows.push([
			"Failure reason",
			job.status === "rolling_back"
				? "The rollback stopped before it finished. It can be run again."
				: "The import stopped before it finished; the questions it did create can be rolled back.",
		]);
	}
	if (job.rolledBackAt) {
		detailRows.push(
			["Rolled back at", new Date(job.rolledBackAt).toLocaleString("en-GB")],
			["Rolled back by", job.rolledBackBy ?? "-"],
		);
	}
	if (skippedIds.size > 0) {
		detailRows.push(["Kept (edited, published or not yet written)", String(skippedIds.size)]);
	}
	if (collisions.length > 0) {
		detailRows.push(["Not overwritten (staged or published)", String(collisions.length)]);
	}

	// Router state, the same way AdminQuestionTable passes filters to and from a detail page.
	// The Questions page reads it only when the URL has no query string. Drafts are
	// included because the Questions page includes them by default and most imports are drafts.
	const questionsListState: { filters: SearchParams } | undefined = job.paperCode
		? { filters: { limit: QUESTIONS_PAGE_LIMIT, offset: 0, includeDrafts: true, paperCode: job.paperCode } }
		: undefined;

	return (
		<section className="space-y-6">
			<div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
				<div>
					<Link to={backLink} className="text-sm font-semibold text-(--admin-color-primary-strong) hover:underline">
						← Back to import jobs
					</Link>
					<h2 ref={headingRef} tabIndex={-1} className="mt-1 text-[1.75rem] font-bold text-(--admin-color-primary)">
						Import job
					</h2>
				</div>
				{canRollBack && (
					<button
						ref={rollbackButtonRef}
						type="button"
						onClick={() => setIsConfirmOpen(true)}
						className="cursor-pointer rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-700"
					>
						Roll back import
					</button>
				)}
			</div>

			<dl className="space-y-2 rounded-lg border border-slate-200 bg-white p-6 text-sm shadow-(--admin-shadow-raised)">
				{detailRows.map(([label, value]) => (
					<div key={label} className="flex gap-4">
						<dt className="w-44 shrink-0 font-medium text-slate-500">{label}</dt>
						<dd className="text-slate-800">{value}</dd>
					</div>
				))}
			</dl>

			<section aria-labelledby="created-questions-heading">
				<h3 id="created-questions-heading" className="text-base font-semibold text-slate-800">
					Created questions
				</h3>
				{job.createdQuestionIds.length === 0 ? (
					<p className="mt-2 text-sm text-slate-600">No questions were created.</p>
				) : (
					<>
						{isPdf && (
							<Link
								to={`/${locale}/admin/import-jobs/${job.id}/review`}
								className="mt-2 mr-4 inline-block text-sm font-semibold text-(--admin-color-primary-strong) hover:underline"
							>
								Review against source PDF
							</Link>
						)}
						<Link
							to={`/${locale}/admin/questions`}
							state={questionsListState}
							className="mt-2 inline-block text-sm font-semibold text-(--admin-color-primary-strong) hover:underline"
						>
							{job.paperCode ? "View these questions in the Questions list" : "View the Questions list"}
						</Link>
						<ul className="mt-2 space-y-1 text-sm">
							{job.createdQuestionIds.map((questionId) => (
								<li key={questionId}>
									<code className="text-slate-800">{questionId}</code>
									{skippedIds.has(questionId) && (
										<span className="ml-1 text-slate-600">(kept - edited, published or not yet written)</span>
									)}
								</li>
							))}
						</ul>
					</>
				)}
			</section>

			{collisions.length > 0 && (
				<section aria-labelledby="collisions-heading">
					<h3 id="collisions-heading" className="text-base font-semibold text-slate-800">
						Not overwritten
					</h3>
					<p className="mt-2 text-sm text-slate-600">
						These questions are already staged or published, so this import left them unchanged.
					</p>
					<ul className="mt-2 space-y-1 text-sm">
						{collisions.map((collision) => (
							<li key={collision.questionId}>
								<code className="text-slate-800">{collision.questionId}</code>
								<span className="ml-1 text-slate-600">({collision.existingStatus})</span>
							</li>
						))}
					</ul>
				</section>
			)}

			{isConfirmOpen && (
				<RollbackConfirmDialog
					isRollingBack={isRollingBack}
					onConfirm={() => void handleRollBack()}
					onCancel={closeConfirm}
				/>
			)}
		</section>
	);
};
